import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition, Observable } from '@apollo/client/utilities';
import { Kind, OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import { createContext, useContext, useMemo } from 'react';

import { useKeycloakAuth } from '@/auth/KeycloakProvider';
import { config } from '@/environment';
import { captureSentry } from '@/utils/helpers/sentryHelper';

function isWsCloseEvent(event: unknown): event is CloseEvent {
  return (
    typeof event === 'object' &&
    event !== null &&
    'code' in event &&
    typeof (event as any).code === 'number' &&
    'reason' in event &&
    'wasClean' in event
  );
}

// NOTE: adds timeouts to apollo requests
export const timeoutLink = (timeoutMs = 5000) =>
  new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeoutMs);

      operation.setContext(({ fetchOptions = {} }) => ({
        fetchOptions: {
          ...fetchOptions,
          signal: controller.signal,
        },
      }));

      const sub = forward(operation).subscribe({
        next: (v) => observer.next(v),
        error: (e) => observer.error(e),
        complete: () => observer.complete(),
      });

      return () => {
        clearTimeout(id);
        controller.abort();
        sub.unsubscribe();
      };
    });
  });

/**
 * AUTH LINK
 * Ensures every HTTP request receives a fresh access token.
 * Using the new shared refresh promise prevents duplicate network calls.
 */
const useAuthLink = () => {
  const tokenCtx = useKeycloakAuth();

  return useMemo(
    () =>
      setContext(async (_, { headers }) => {
        const token = await tokenCtx.getFreshToken();

        return {
          headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : '',
          },
        };
      }),
    [tokenCtx.getFreshToken]
  );
};

/**
 * WEBSOCKET LINK
 * Uses same getFreshToken so subscriptions remain authenticated
 * even after a refresh happens.
 */
const useWsLink = (backendUrl: string) => {
  const tokenCtx = useKeycloakAuth();

  return useMemo(() => {
    return new GraphQLWsLink(
      createClient({
        url: `${backendUrl.replace('http', 'ws')}/graphql`,
        connectionParams: async () => {
          const token = await tokenCtx.getFreshToken();
          return {
            Authorization: token ? `Bearer ${token}` : '',
          };
        },
        keepAlive: 15000,
        retryAttempts: Infinity, // try reconnecting indefinitely
        shouldRetry: () => true, // always retry on connection failure
        on: {
          connected: () => console.log('WebSocket connected'),
          closed: (event) => {
            if (!isWsCloseEvent(event)) {
              captureSentry({
                title: 'WebSocket closed (unknown event type)',
                detail: { backendUrl, rawEvent: event },
              });
              return;
            }
            if (event.code === 1000) return;
            if (event.wasClean) return;

            captureSentry({
              title: 'WebSocket closed',
              detail: { backendUrl, rawEvent: event },
            });
          },
          error: (err) => {
            captureSentry({
              title: 'WebSocket error',
              error: err as Error,
              detail: { backendUrl },
            });
          },
        },
      })
    );
  }, [backendUrl, tokenCtx.getFreshToken]);
};

type ApolloClientContextType = {
  apolloClient: ApolloClient<NormalizedCacheObject>;
};

export const ApolloClientContext = createContext<ApolloClientContextType | undefined>(undefined);

export function CustomApolloProvider({ children }: { children: React.ReactNode }) {
  const authLink = useAuthLink();
  const wsLink = useWsLink(config.BACKEND_URL);

  const apolloClient = useMemo(() => {
    const httpLink = new HttpLink({
      uri: `${config.BACKEND_URL}/graphql`,
    });

    const link = split(
      ({ query }) => {
        const def = getMainDefinition(query);
        return (
          def.kind === Kind.OPERATION_DEFINITION && def.operation === OperationTypeNode.SUBSCRIPTION
        );
      },
      wsLink,
      from([authLink, httpLink])
    );

    return new ApolloClient({
      link,
      cache: new InMemoryCache({
        typePolicies: {
          User: {
            fields: {
              email: {
                read(existing, { readField }) {
                  if (!existing) return undefined;
                  const isGuest = readField('isGuest');
                  if (isGuest) {
                    // return existing;
                    return existing.replace(/_\d+(?=@)/, '');
                  }
                  return existing;
                },
              },
            },
          },
        },
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          nextFetchPolicy: 'cache-first',
          notifyOnNetworkStatusChange: true,
        },
      },
    });
  }, [authLink, wsLink]);

  return (
    <ApolloClientContext.Provider value={{ apolloClient }}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </ApolloClientContext.Provider>
  );
}

export const useApolloClientContext = () => {
  const context = useContext(ApolloClientContext);
  if (!context) {
    throw new Error('useApolloClient must be used within an ApolloClientProvider');
  }
  return context;
};
